import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Trophy, Clock } from 'lucide-react';
import { formatTime } from '@/lib/dates';

interface Match {
  id: string;
  round_number: number;
  match_number: number;
  bracket_type: string;
  team1_name?: string;
  team2_name?: string;
  team1_score?: number;
  team2_score?: number;
  winner_id?: string;
  status: string;
  scheduled_time?: string;
}

interface BracketVisualizerProps {
  matches: Match[];
  tournamentType: string;
  bracketType: 'all' | 'winners' | 'losers' | 'final';
}

const BracketVisualizer: React.FC<BracketVisualizerProps> = ({ matches, tournamentType, bracketType }) => {
  const getMatchesByRound = () => {
    const roundsMap = new Map<number, Match[]>();
    
    matches.forEach(match => {
      if (!roundsMap.has(match.round_number)) {
        roundsMap.set(match.round_number, []);
      }
      roundsMap.get(match.round_number)!.push(match);
    });

    // Sort matches within each round by match_number
    roundsMap.forEach((matches, round) => {
      matches.sort((a, b) => a.match_number - b.match_number);
    });

    return Array.from(roundsMap.entries()).sort(([a], [b]) => a - b);
  };

  const getWinnerDisplay = (match: Match) => {
    if (match.status !== 'completed') return null;
    
    const team1Won = match.team1_score !== undefined && match.team2_score !== undefined && 
                    match.team1_score > match.team2_score;
    const team2Won = match.team1_score !== undefined && match.team2_score !== undefined && 
                    match.team2_score > match.team1_score;

    return { team1Won, team2Won };
  };

  const MatchCard: React.FC<{ match: Match; roundNumber: number; totalRounds: number }> = ({ 
    match, 
    roundNumber, 
    totalRounds 
  }) => {
    const winner = getWinnerDisplay(match);
    const isCompleted = match.status === 'completed';
    const isLive = match.status === 'in_progress';
    const isFinal = roundNumber === totalRounds;

    return (
      <Card className={`relative ${isLive ? 'border-primary' : ''} ${isFinal ? 'border-2 border-primary' : ''}`}>
        <CardContent className="p-4 space-y-3">
          {/* Match Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {isFinal && <Trophy className="h-4 w-4 text-primary" />}
              <span className="text-xs font-medium">
                {isFinal ? 'Final' : `Round ${roundNumber}`}
              </span>
              {isLive && <Badge variant="destructive" className="text-xs">Live</Badge>}
              {isCompleted && <Badge variant="outline" className="text-xs">Final</Badge>}
            </div>
            {match.scheduled_time && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Clock className="h-3 w-3" />
                {formatTime(match.scheduled_time)}
              </div>
            )}
          </div>

          {/* Team 1 */}
          <div className={`flex items-center justify-between p-2 rounded border-l-4 ${
            winner?.team1Won ? 'border-l-primary bg-primary/5' : 'border-l-muted'
          }`}>
            <span className={`font-medium ${winner?.team1Won ? 'text-primary' : ''}`}>
              {match.team1_name || 'TBD'}
            </span>
            {isCompleted && match.team1_score !== undefined && (
              <span className={`font-bold ${winner?.team1Won ? 'text-primary' : ''}`}>
                {match.team1_score}
              </span>
            )}
          </div>

          {/* Team 2 */}
          <div className={`flex items-center justify-between p-2 rounded border-l-4 ${
            winner?.team2Won ? 'border-l-primary bg-primary/5' : 'border-l-muted'
          }`}>
            <span className={`font-medium ${winner?.team2Won ? 'text-primary' : ''}`}>
              {match.team2_name || 'TBD'}
            </span>
            {isCompleted && match.team2_score !== undefined && (
              <span className={`font-bold ${winner?.team2Won ? 'text-primary' : ''}`}>
                {match.team2_score}
              </span>
            )}
          </div>

          {/* Additional match info */}
          {match.bracket_type !== 'winners' && match.bracket_type !== 'pool' && (
            <Badge variant="outline" className="text-xs capitalize">
              {match.bracket_type}
            </Badge>
          )}
        </CardContent>
      </Card>
    );
  };

  if (matches.length === 0) {
    return (
      <div className="text-center py-12">
        <Trophy className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
        <h3 className="text-xl font-semibold mb-2">No Matches Available</h3>
        <p className="text-muted-foreground">
          The bracket will be displayed once matches are scheduled.
        </p>
      </div>
    );
  }

  const rounds = getMatchesByRound();

  if (tournamentType === 'single_elimination' || bracketType === 'winners' || bracketType === 'losers' || bracketType === 'final') {
    return (
      <div className="space-y-6">
        <div className="overflow-x-auto">
          <div className="flex gap-6 min-w-max p-4">
            {rounds.map(([roundNumber, roundMatches]) => (
              <div key={roundNumber} className="space-y-4 min-w-[280px]">
                <h3 className="text-lg font-semibold text-center">
                  {roundNumber === rounds.length ? 'Final' : `Round ${roundNumber}`}
                </h3>
                <div className="space-y-4">
                  {roundMatches.map((match) => (
                    <MatchCard 
                      key={match.id} 
                      match={match} 
                      roundNumber={roundNumber}
                      totalRounds={rounds.length}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (tournamentType === 'double_elimination' && bracketType === 'all') {
    const winnersMatches = matches.filter(m => m.bracket_type === 'winners');
    const losersMatches = matches.filter(m => m.bracket_type === 'losers');
    const finalMatches = matches.filter(m => m.bracket_type === 'final');

    return (
      <div className="space-y-8">
        {/* Winners Bracket */}
        <div>
          <h3 className="text-xl font-bold mb-4">Winners Bracket</h3>
          <BracketVisualizer 
            matches={winnersMatches} 
            tournamentType={tournamentType} 
            bracketType="winners" 
          />
        </div>

        {/* Losers Bracket */}
        <div>
          <h3 className="text-xl font-bold mb-4">Losers Bracket</h3>
          <BracketVisualizer 
            matches={losersMatches} 
            tournamentType={tournamentType} 
            bracketType="losers" 
          />
        </div>

        {/* Grand Final */}
        {finalMatches.length > 0 && (
          <div>
            <h3 className="text-xl font-bold mb-4">Grand Final</h3>
            <BracketVisualizer 
              matches={finalMatches} 
              tournamentType={tournamentType} 
              bracketType="final" 
            />
          </div>
        )}
      </div>
    );
  }

  // Default grid layout for other tournament types
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {matches.map((match) => (
        <MatchCard 
          key={match.id} 
          match={match} 
          roundNumber={match.round_number}
          totalRounds={Math.max(...matches.map(m => m.round_number))}
        />
      ))}
    </div>
  );
};

export default BracketVisualizer;